import { Box, Grid, Typography, IconButton, Card, useMediaQuery, Button, CardMedia, CardContent, Stack, Chip, Tooltip, LinearProgress } from "@mui/material";
import { useSelector, useDispatch } from "react-redux"
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import SnackbarMessage from "../../../components/snackBar/snackBar";
import api from "../../../config/api";
import { API_ROUTES } from "../../../config/apiRoutes";
import DataTableWithCheckbox from "../../../components/dataTableWithSearch/DataTableWithCheckBox";
import * as XLSX from 'xlsx';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { formatDate } from "../../../components/formatDateAndTime/formatDateAndTime";
import { setViewCampaign } from "../campaignSlice";
import { formatLatestDate } from "../../../components/formatDateAndTime/formatDateAndTime";
import { getCampaignStatusThunk } from "../campaignThunk";
import { APP_ROUTES } from "../../../config/appRoutes";
import PhoneIcon from "@mui/icons-material/Phone";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ChatIcon from '@mui/icons-material/Chat';
import { formatDateWithTimeWithoutSec } from "../../../components/formatDateAndTime/formatDateAndTime";
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import DoneRoundedIcon from '@mui/icons-material/DoneRounded';
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import convoBg from '../../../assests/convoBg.jpg';

// Shared look (presentation only)
const cardSx = { borderRadius: 3, border: "1px solid #e9edf2", boxShadow: "0 1px 3px rgba(16,24,40,0.06)" };

const statusTone = (status = "") => {
    const s = status.toLowerCase();
    if (s === "completed") return { bg: "#ecfdf5", color: "#047857", dot: "#10b981" };
    if (s.includes("cancel") || s.includes("fail")) return { bg: "#fef2f2", color: "#b91c1c", dot: "#ef4444" };
    if (s.includes("schedul") || s.includes("pending")) return { bg: "#fffbeb", color: "#b45309", dot: "#f59e0b" };
    return { bg: "#eff6ff", color: "#1d4ed8", dot: "#3b82f6" };
};

export default function ViewCampaign() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const { viewCampaignDetails = [], campaignStatusUsers = [] } = useSelector((state) => state.campaigns);
    const isMobile = useMediaQuery("(max-width:600px)");
    const campaign = viewCampaignDetails?.[0] || {};
    const { campaign_name, template_form, notification_type, updated_time, inserted_time, scheduled_time, include, id, exclude, workflow_status } = campaign;
    const isCompleted = workflow_status?.toLowerCase() === "completed";
    const tone = statusTone(workflow_status);

    // Recipient-wise send status (outbond_campaign_message)
    const [messages, setMessages] = useState([]);
    const [messagesStatus, setMessagesStatus] = useState("idle");
    const [statusFilter, setStatusFilter] = useState("all");

    const [resending, setResending] = useState(null); // "all" | message id | null
    const [notice, setNotice] = useState({ open: false, message: "", severity: "success" });
    const refreshTimers = useRef([]);

    // quiet = refresh without the table loader (after a resend)
    const loadMessages = useCallback((quiet = false) => {
        if (!id) return Promise.resolve();
        if (!quiet) setMessagesStatus("loading");
        return api.post(API_ROUTES.CAMPAIGN_MESSAGES, { campaign_id: id })
            .then((res) => { setMessages(Array.isArray(res.data?.data) ? res.data.data : []); setMessagesStatus("succeeded"); })
            .catch(() => { if (!quiet) { setMessages([]); setMessagesStatus("failed"); } });
    }, [id]);

    useEffect(() => {
        loadMessages();
        const timers = refreshTimers.current;
        return () => timers.forEach(clearTimeout);
    }, [loadMessages]);

    // Failed rows -> pending -> sent again by the worker; refresh a few times to show the result
    const handleResend = async (messageId) => {
        setResending(messageId ?? "all");
        try {
            const res = await api.post(API_ROUTES.CAMPAIGN_RESEND_MESSAGES, {
                campaign_id: id,
                ...(messageId ? { message_ids: [messageId] } : {}),
            });
            setNotice({ open: true, message: res.data?.message || "Queued for resend", severity: "success" });
            await loadMessages(true);
            refreshTimers.current.push(setTimeout(() => loadMessages(true), 5000), setTimeout(() => loadMessages(true), 15000));
        } catch (err) {
            setNotice({ open: true, message: err.response?.data?.message || "Could not resend", severity: "error" });
        } finally {
            setResending(null);
        }
    };

    const sendCounts = useMemo(() => messages.reduce((acc, m) => {
        acc[m.send_status] = (acc[m.send_status] || 0) + 1;
        return acc;
    }, {}), [messages]);

    const visibleMessages = useMemo(
        () => (statusFilter === "all" ? messages : messages.filter((m) => m.send_status === statusFilter)),
        [messages, statusFilter]
    );

    const sendTone = {
        sent: { bg: "#ecfdf5", color: "#047857", dot: "#10b981", label: "Sent" },
        failed: { bg: "#fef2f2", color: "#b91c1c", dot: "#ef4444", label: "Failed" },
        pending: { bg: "#fffbeb", color: "#b45309", dot: "#f59e0b", label: "Pending" },
        processing: { bg: "#eff6ff", color: "#1d4ed8", dot: "#3b82f6", label: "Sending" },
        cancelled: { bg: "#f1f5f9", color: "#475569", dot: "#94a3b8", label: "Cancelled" },
    };

    const messageColumns = useMemo(() => [
        { header: "SNO", accessorKey: "sno" },
        { header: "Name", accessorKey: "contact_name", Cell: ({ row }) => row.original.contact_name || "-" },
        { header: "Mobile", accessorKey: "mobile" },
        {
            header: "Send Status", accessorKey: "send_status",
            Cell: ({ row }) => {
                const t = sendTone[row.original.send_status] || sendTone.pending;
                return (
                    <Chip
                        size="small"
                        label={t.label}
                        icon={<Box component="span" sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: t.dot, ml: "8px !important" }} />}
                        sx={{ height: 24, fontWeight: 700, fontSize: 12, bgcolor: t.bg, color: t.color }}
                    />
                );
            },
        },
        {
            header: "Delivery", accessorKey: "delivery_status",
            Cell: ({ row }) => (
                <Typography sx={{ fontSize: 13, color: row.original.delivery_status ? "#0f172a" : "#94a3b8", textTransform: "capitalize" }}>
                    {row.original.delivery_status || "-"}
                </Typography>
            ),
        },
        { header: "Sent At", accessorKey: "sent_time", Cell: ({ row }) => (row.original.sent_time ? formatDateWithTimeWithoutSec(row.original.sent_time) : "-") },
        {
            header: "Reason", accessorKey: "error_message",
            Cell: ({ row }) => row.original.error_message
                ? <Tooltip title={row.original.error_message}><Typography noWrap sx={{ fontSize: 12.5, color: "#b91c1c", maxWidth: 260 }}>{row.original.error_message}</Typography></Tooltip>
                : <Typography sx={{ fontSize: 13, color: "#94a3b8" }}>-</Typography>,
        },
        {
            header: "Action", accessorKey: "id", enableGlobalFilter: false,
            Cell: ({ row }) => row.original.send_status === "failed" ? (
                <Button
                    size="small"
                    startIcon={<ReplayRoundedIcon sx={{ fontSize: 16 }} />}
                    disabled={!!resending}
                    onClick={() => handleResend(row.original.id)}
                    sx={{ height: 28, px: 1.25, borderRadius: 2, textTransform: "none", fontWeight: 700, fontSize: 12.5, color: "#b91c1c", bgcolor: "#fef2f2", border: "1px solid #fecaca", "&:hover": { bgcolor: "#fee2e2" } }}
                >
                    {resending === row.original.id ? "Resending…" : "Resend"}
                </Button>
            ) : <Typography sx={{ fontSize: 13, color: "#cbd5e1" }}>-</Typography>,
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [resending]);

    const renderMessageFilters = (
        <Stack direction="row" flexWrap="wrap" gap={0.75}>
            {[
                { value: "all", label: "All", count: messages.length },
                ...["sent", "failed", "pending", "processing", "cancelled"]
                    .filter((s) => sendCounts[s])
                    .map((s) => ({ value: s, label: sendTone[s].label, count: sendCounts[s] })),
            ].map((f) => {
                const active = statusFilter === f.value;
                const t = sendTone[f.value];
                return (
                    <Chip
                        key={f.value}
                        label={`${f.label} · ${f.count}`}
                        onClick={() => setStatusFilter(f.value)}
                        sx={{
                            height: 30, fontWeight: 700, fontSize: 12.5, cursor: "pointer",
                            bgcolor: active ? (t?.color || "#2563eb") : "#fff",
                            color: active ? "#fff" : (t?.color || "#334155"),
                            border: `1px solid ${active ? (t?.color || "#2563eb") : "#e2e8f0"}`,
                            "&:hover": { bgcolor: active ? (t?.color || "#2563eb") : "#f8fafc" },
                        }}
                    />
                );
            })}
        </Stack>
    );

    useEffect(() => {
        if (viewCampaignDetails.length !== 0) {
            dispatch(getCampaignStatusThunk({ credentials: { campaign_id: id } }));
        } else {
            navigate(APP_ROUTES.CAMPAIGN.LIST);
        }
    }, [viewCampaignDetails, dispatch, navigate]);


    const handleQrExcelDownload = () => {
        const formattedData = campaignStatusUsers.map((item) => ({
            "Name": item.name,
            "Mobile": item.mobile,
            "campaign Name": campaign_name,
            "Status": item.message_status,
            "Inserted Time": formatDate(inserted_time),
        }));

        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(formattedData);
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
        XLSX.writeFile(workbook, `${campaign_name}.xlsx`);
    };

    const formatText = (text) => {
        if (!text || typeof text !== 'string') {
            return '';
        }
        return text.replace(/\*([^*]+)\*/g, '<strong>$1</strong>').replace(/\\n/g, '\n')
    };

    useEffect(() => {
        if (location.pathname !== "/view-campaign") {
            dispatch(setViewCampaign([]))
        }
    }, [dispatch]);

    // Delivery funnel tiles
    const attempted = Number(campaign.Attempted) || 0;
    const pct = (n) => (attempted > 0 ? Math.round((Number(n || 0) / attempted) * 100) : 0);
    const stats = [
        { label: "Attempted", value: campaign.Attempted, icon: <SendRoundedIcon />, color: "#475569", bg: "#f1f5f9" },
        { label: "Sent", value: campaign.Sent, icon: <DoneRoundedIcon />, color: "#2563eb", bg: "#eff6ff", showPct: true },
        { label: "Delivered", value: campaign.Delivered, icon: <DoneAllRoundedIcon />, color: "#0891b2", bg: "#ecfeff", showPct: true },
        { label: "Read", value: campaign.Read, icon: <VisibilityOutlinedIcon />, color: "#059669", bg: "#ecfdf5", showPct: true },
        { label: "Failed", value: campaign.Failed, icon: <ErrorOutlineRoundedIcon />, color: "#dc2626", bg: "#fef2f2", showPct: true },
    ];

    const renderTags = (tags, variant) => {
        const list = tags?.split(",").map((t) => t.trim()).filter(Boolean) || [];
        if (list.length === 0) return <Typography sx={{ fontSize: 13.5, color: "#94a3b8" }}>None</Typography>;
        const c = variant === "include"
            ? { bg: "#ecfdf5", color: "#047857", border: "#a7f3d0" }
            : { bg: "#fef2f2", color: "#b91c1c", border: "#fecaca" };
        return (
            <Stack direction="row" flexWrap="wrap" gap={0.75}>
                {list.map((item, idx) => (
                    <Chip key={idx} label={item} size="small" sx={{ height: 24, fontWeight: 600, fontSize: 12, bgcolor: c.bg, color: c.color, border: `1px solid ${c.border}` }} />
                ))}
            </Stack>
        );
    };


    return (
        <Box>
            {/* Header: back + name + status, download on the right */}
            <Card
                sx={{
                    ...cardSx,
                    mb: 1.5,
                    position: "relative",
                    overflow: "hidden",
                    background: "linear-gradient(100deg, #eff6ff 0%, #f8fbff 45%, #ffffff 100%)",
                    // left accent bar
                    "&::before": { content: '""', position: "absolute", left: 0, top: 0, bottom: 0, width: 4, background: "linear-gradient(180deg, #2563eb, #60a5fa)" },
                    // soft decorative rings on the right
                    "&::after": {
                        content: '""', position: "absolute", right: { xs: -60, sm: 140 }, top: -70, width: 180, height: 180, borderRadius: "50%",
                        border: "22px solid rgba(37,99,235,0.05)", pointerEvents: "none",
                    },
                }}
            >
                <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "stretch", sm: "center" }} justifyContent="space-between" spacing={1.5} sx={{ position: "relative", zIndex: 1, pl: { xs: 1.75, sm: 2.25 }, pr: { xs: 1.25, sm: 2 }, py: 1.5 }}>
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
                        <IconButton onClick={() => navigate(-1)} sx={{ bgcolor: "#fff", border: "1px solid #e2e8f0", borderRadius: 2, color: "#334155", boxShadow: "0 1px 2px rgba(16,24,40,0.05)", "&:hover": { bgcolor: "#f1f5f9" } }}>
                            <ArrowBackRoundedIcon fontSize="small" />
                        </IconButton>
                        <Box sx={{ display: { xs: "none", sm: "grid" }, placeItems: "center", width: 44, height: 44, flexShrink: 0, borderRadius: 2.5, color: "#fff", background: "linear-gradient(135deg, #2563eb, #60a5fa)", boxShadow: "0 8px 16px -8px rgba(37,99,235,0.7)" }}>
                            <CampaignOutlinedIcon sx={{ fontSize: 23 }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                                <Typography noWrap sx={{ fontWeight: 800, fontSize: { xs: 16, sm: 18 }, color: "#0f172a", textTransform: "capitalize", lineHeight: 1.3, minWidth: 0 }}>
                                    {campaign_name}
                                </Typography>
                                {workflow_status && (
                                    <Chip
                                        size="small"
                                        label={workflow_status}
                                        icon={<Box component="span" sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: tone.dot, ml: "8px !important", boxShadow: `0 0 0 3px ${tone.dot}33` }} />}
                                        sx={{ height: 22, flexShrink: 0, fontWeight: 700, fontSize: 11.5, bgcolor: tone.bg, color: tone.color, border: `1px solid ${tone.dot}40`, textTransform: "capitalize" }}
                                    />
                                )}
                            </Stack>
                            {/* meta: notification type · created date */}
                            <Stack direction="row" alignItems="center" flexWrap="wrap" columnGap={1.5} rowGap={0.25} sx={{ mt: 0.25, color: "#64748b", "& svg": { fontSize: 15, color: "#94a3b8" } }}>
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <NotificationsActiveOutlinedIcon />
                                    <Typography sx={{ fontSize: 12.5, textTransform: "capitalize" }}>{notification_type || "-"}</Typography>
                                </Stack>
                                <Box sx={{ width: 3, height: 3, borderRadius: "50%", bgcolor: "#cbd5e1" }} />
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <EventOutlinedIcon />
                                    <Typography sx={{ fontSize: 12.5 }}>Created {formatLatestDate(inserted_time) ?? "-"}</Typography>
                                </Stack>
                            </Stack>
                        </Box>
                    </Stack>

                    <Tooltip title={isCompleted ? "" : "Available once the campaign is completed"}>
                        <span>
                            <Button
                                startIcon={<FileDownloadOutlinedIcon />}
                                onClick={isCompleted ? handleQrExcelDownload : undefined}
                                disabled={!isCompleted}
                                fullWidth={isMobile}
                                sx={{
                                    height: 38, px: 2.25, borderRadius: 2.5, textTransform: "none", fontWeight: 700, fontSize: 14,
                                    color: "#fff", background: "linear-gradient(135deg, #2563eb, #3b82f6)",
                                    "&:hover": { background: "linear-gradient(135deg, #1d4ed8, #2563eb)", boxShadow: "0 8px 18px -8px #2563eb" },
                                    "&.Mui-disabled": { color: "#94a3b8", background: "#fff", border: "1px dashed #cbd5e1" },
                                }}
                            >
                                Download Report
                            </Button>
                        </span>
                    </Tooltip>
                </Stack>
            </Card>

            {/* Delivery stats */}
            <Grid container spacing={1.25} sx={{ mb: 1.5 }}>
                {stats.map((s) => (
                    <Grid item key={s.label} size={{ xs: 6, sm: 4, md: 2.4 }}>
                        <Card sx={{ ...cardSx, px: 1.5, py: 1.25, height: "100%" }}>
                            <Stack direction="row" alignItems="center" spacing={1.25}>
                                <Box sx={{ width: 34, height: 34, flexShrink: 0, borderRadius: 2, display: "grid", placeItems: "center", bgcolor: s.bg, color: s.color, "& svg": { fontSize: 18 } }}>
                                    {s.icon}
                                </Box>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#64748b", lineHeight: 1.2 }}>{s.label}</Typography>
                                    <Stack direction="row" alignItems="baseline" spacing={0.5}>
                                        <Typography sx={{ fontSize: 19, fontWeight: 800, color: "#0f172a", lineHeight: 1.3 }}>{s.value ?? 0}</Typography>
                                        {s.showPct && <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: s.color }}>{pct(s.value)}%</Typography>}
                                    </Stack>
                                </Box>
                            </Stack>
                            <LinearProgress
                                variant="determinate"
                                value={s.showPct ? pct(s.value) : attempted > 0 ? 100 : 0}
                                sx={{ mt: 1, height: 3, borderRadius: 3, bgcolor: "#f1f5f9", "& .MuiLinearProgress-bar": { borderRadius: 3, bgcolor: s.color } }}
                            />
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <Grid container spacing={1.5} alignItems="flex-start">
                {/* Details: one compact card, label on the left, value on the right */}
                <Grid item size={{ xs: 12, md: 7 }}>
                    <Card sx={cardSx}>
                        <Box sx={{ px: 2, py: 1.25, borderBottom: "1px solid #eef2f6" }}>
                            <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: "#0f172a" }}>Campaign Info</Typography>
                        </Box>
                        {[
                            {
                                icon: <NotificationsActiveOutlinedIcon />, label: "Notification Type",
                                value: <Chip label={notification_type || "-"} size="small" sx={{ height: 24, fontWeight: 600, fontSize: 12.5, bgcolor: "#f1f5f9", color: "#334155", textTransform: "capitalize" }} />,
                            },
                            { icon: <LocalOfferOutlinedIcon />, label: "Include Tags", value: renderTags(include, "include") },
                            { icon: <LocalOfferOutlinedIcon />, label: "Exclude Tags", value: renderTags(exclude, "exclude") },
                            { icon: <ScheduleRoundedIcon />, label: "Started on", value: formatLatestDate(inserted_time) ?? "-" },
                            { icon: <ScheduleRoundedIcon />, label: "Ended on", value: formatDateWithTimeWithoutSec(scheduled_time) ?? formatLatestDate(updated_time) },
                        ].map((row, i) => (
                            <Stack
                                key={row.label}
                                direction={{ xs: "column", sm: "row" }}
                                spacing={{ xs: 0.75, sm: 2 }}
                                alignItems={{ xs: "flex-start", sm: "center" }}
                                sx={{ px: 2, py: 1.25, borderTop: i > 0 ? "1px solid #f1f5f9" : "none" }}
                            >
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ width: { sm: 170 }, flexShrink: 0, color: "#94a3b8", "& svg": { fontSize: 17 } }}>
                                    {row.icon}
                                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#64748b" }}>{row.label}</Typography>
                                </Stack>
                                <Box sx={{ fontSize: 13.5, fontWeight: 600, color: "#0f172a", minWidth: 0 }}>{row.value || "-"}</Box>
                            </Stack>
                        ))}
                    </Card>
                </Grid>

                {/* Template preview */}
                <Grid item size={{ xs: 12, md: 5 }}>
                    <Card sx={cardSx}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 2, py: 1.25, borderBottom: "1px solid #eef2f6" }}>
                            <WhatsAppIcon sx={{ fontSize: 19, color: "#16a34a" }} />
                            <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: "#0f172a" }}>Message Preview</Typography>
                        </Stack>
                        <Box sx={{ p: { xs: 1.25, sm: 1.75 }, bgcolor: "#efeae2", backgroundImage: `url(${convoBg})`, backgroundRepeat: "repeat", backgroundSize: "300px auto" }}>
                            <Card sx={{ width: "100%", maxWidth: 440, mx: "auto", overflowWrap: "anywhere", borderRadius: "12px", bgcolor: "#d9fdd3", boxShadow: "0 1px 0.5px rgba(11,20,26,0.13)" }}>
                                {Array.isArray(template_form) && template_form.length > 0 ? (
                                    template_form.map((item, index) => {
                                        const type = item.type?.toLowerCase();
                                        const format = item.format?.toLowerCase();
                                        const headerHandle = item.example?.header_handle?.[0];

                                        return (
                                            <Box key={index}>
                                                {/* ===== Header Section ===== */}
                                                {type === "header" && format === "text" && (
                                                    <CardContent sx={{ pb: 0 }}>
                                                        <Typography
                                                            variant="subtitle1"
                                                            fontWeight="bold"
                                                            color="text.primary"
                                                            whiteSpace="pre-line"
                                                            dangerouslySetInnerHTML={{ __html: formatText(item.text) }}
                                                        />
                                                    </CardContent>
                                                )}

                                                {type === "header" && format === "image" && headerHandle && (
                                                    <Box sx={{ p: 0.5 }}>
                                                        <CardMedia
                                                            component="img"
                                                            image={headerHandle}
                                                            alt="Header Image"
                                                            sx={{ maxHeight: 260, width: "100%", objectFit: "cover", borderRadius: "9px" }}
                                                        />
                                                    </Box>
                                                )}

                                                {type === "header" && format === "video" && headerHandle && (
                                                    <Box sx={{ p: 0.5 }}>
                                                        <CardMedia component="video" controls sx={{ maxHeight: 260, width: "100%", borderRadius: "9px", bgcolor: "#000" }}>
                                                            <source src={headerHandle} type="video/mp4" />
                                                        </CardMedia>
                                                    </Box>
                                                )}

                                                {type === "header" && format === "document" && headerHandle && (
                                                    <CardContent sx={{ textAlign: "center" }}>
                                                        <a href={headerHandle} target="_blank" rel="noopener noreferrer">
                                                            View Document
                                                        </a>
                                                    </CardContent>
                                                )}

                                                {/* ===== Body Section ===== */}
                                                {type === "body" && (
                                                    <CardContent sx={{ py: 1.25, "&:last-child": { pb: 1.25 } }}>
                                                        <Typography
                                                            sx={{ fontSize: 13.5, lineHeight: 1.5, color: "#111b21" }}
                                                            whiteSpace="pre-line"
                                                            dangerouslySetInnerHTML={{ __html: formatText(item.text) }}
                                                        />
                                                    </CardContent>
                                                )}

                                                {/* ===== Footer Section ===== */}
                                                {type === "footer" && (
                                                    <CardContent sx={{ pt: 0, pb: 1.25, "&:last-child": { pb: 1.25 } }}>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            dangerouslySetInnerHTML={{ __html: formatText(item.text) }}
                                                        />
                                                    </CardContent>
                                                )}

                                                {/* ===== Buttons Section ===== */}
                                                {type === "buttons" && item.buttons?.length > 0 && (
                                                    <Box sx={{ borderTop: "1px solid rgba(11,20,26,0.1)" }}>
                                                        {item.buttons.map((button, btnIndex) => {
                                                            const btnSx = {
                                                                justifyContent: "center", textTransform: "none", fontWeight: 600, fontSize: 13.5,
                                                                color: "#027eb5", borderRadius: 0, py: 0.75,
                                                                borderTop: btnIndex > 0 ? "1px solid rgba(11,20,26,0.1)" : "none",
                                                                "&:hover": { bgcolor: "rgba(2,126,181,0.06)" },
                                                            };
                                                            return (
                                                                <Box key={btnIndex}>
                                                                    {button.type === "url" && (
                                                                        <Button fullWidth href={button.url} target="_blank" rel="noopener noreferrer" startIcon={<OpenInNewIcon />} sx={btnSx}>
                                                                            {button.text}
                                                                        </Button>
                                                                    )}

                                                                    {button.type === "PHONE_NUMBER" && (
                                                                        <Button fullWidth href={`tel:${button.phone_number}`} startIcon={<PhoneIcon />} sx={btnSx}>
                                                                            {button.text}
                                                                        </Button>
                                                                    )}

                                                                    {button.type === "QUICK_REPLY" && (
                                                                        <Button fullWidth startIcon={<ChatIcon />} sx={btnSx}>
                                                                            {button.text}
                                                                        </Button>
                                                                    )}
                                                                </Box>
                                                            );
                                                        })}
                                                    </Box>
                                                )}
                                            </Box>
                                        );
                                    })
                                ) : (
                                    <Typography sx={{ textAlign: "center", py: 2, color: "#64748b" }}>No template form data</Typography>
                                )}
                            </Card>
                        </Box>
                    </Card>
                </Grid>
            </Grid>

            {/* Recipients: send status per message */}
            <Card sx={{ ...cardSx, mt: 1.5 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.25, borderBottom: "1px solid #eef2f6" }}>
                    <Box>
                        <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: "#0f172a" }}>Recipients</Typography>
                        <Typography sx={{ fontSize: 12.5, color: "#64748b" }}>Send status of every message in this campaign</Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                        {sendCounts.failed > 0 && (
                            <Button
                                size="small"
                                startIcon={<ReplayRoundedIcon sx={{ fontSize: 17 }} />}
                                disabled={!!resending}
                                onClick={() => handleResend()}
                                sx={{ height: 32, px: 1.75, borderRadius: 2, textTransform: "none", fontWeight: 700, fontSize: 13, color: "#fff", background: "linear-gradient(135deg, #dc2626, #ef4444)", "&:hover": { background: "linear-gradient(135deg, #b91c1c, #dc2626)" }, "&.Mui-disabled": { color: "#fff", opacity: 0.6 } }}
                            >
                                {resending === "all" ? "Resending…" : `Resend all failed (${sendCounts.failed})`}
                            </Button>
                        )}
                        <Chip label={`${messages.length} messages`} size="small" sx={{ fontWeight: 700, fontSize: 12, bgcolor: "#eff6ff", color: "#1d4ed8" }} />
                    </Stack>
                </Stack>
                <Box sx={{ p: { xs: 1.25, sm: 2 } }}>
                    <DataTableWithCheckbox
                        status={messagesStatus}
                        enableRowSelection={false}
                        columns={messageColumns}
                        data={visibleMessages}
                        renderFilters={renderMessageFilters}
                        searchFirst
                        emptyMessage={messagesStatus === "failed" ? "Could not load messages." : "No messages for this campaign."}
                    />
                </Box>
            </Card>

            <SnackbarMessage
                open={notice.open}
                message={notice.message}
                severity={notice.severity}
                onClose={() => setNotice((prev) => ({ ...prev, open: false }))}
            />
        </Box>
    )
}
